import React from 'react';
import styled, { css } from 'styled-components';
import { camelCase } from 'lodash-es';
import {
  FlexBox,
  BaseBox,
  Heading3,
  PrimaryButton,
  SecondaryButton,
  Body1,
  Icons,
  DoconModal,
  DoconModalContent,
} from '@docon/docon-ui-library';
import { colors } from 'utilities/colorConfig';

interface IProps {
  isLoading?: boolean;
  requestType: string;
  doctorName?: string;
  onContactUsClick: () => void;
  onConfirmClick: () => void;
  onClose: () => void;
}

const Header = styled(BaseBox)`
  background: ${colors.primaryBlue};
  padding: 20px 16px 16px;
  border-radius: 10px 10px 0 0;

  ${Heading3} { 
    margin: 0;
    color: ${colors.white};
    font-weight: bold;
    text-align: center;
  }
`;

const Content = styled(Body1)`
  padding: 27px 16px 23px;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.primaryColor};
  text-align: center;
`;

const Footer = styled(FlexBox)`
  justify-content: center;

  button {
    margin: 0;
    width: 100%;
    flex: 1;
    max-width: 50%;
    font-weight: 600;
  }

  ${({ $requestType }: any) => $requestType === 'confirmCancellation' && css`
    flex-flow: row-reverse;
  `}
`;

const Modal = styled<any>(DoconModal)`
  max-width: calc(100vw - 50px);
  border-radius: 10px;

  ${DoconModalContent} {
    padding: 0;
  }
`;

const RequestCancellation = ({
  isLoading,
  requestType,
  doctorName,
  onContactUsClick,
  onConfirmClick,
  onClose,
}: IProps) => {

  function handleBtnClick(event: any) {
    const key = event?.currentTarget?.id;

    switch (key) {
      case 'contactUs':
        return onContactUsClick();
      case 'confirm':
        return onConfirmClick();
      case 'close':
        return onClose();
      default:
        break;
    }

  }

  function renderedContent() {
    let heading = '';
    let subHeading = '';
    let text = '';
    let primaryButtonTitle = 'Close';
    let secondaryButtonTitle = 'Contact us';
    switch (requestType) {
      case 'eligible':
        heading = 'Confirm cancellation';
        subHeading = 'We have accepted your request and you are <b>eligible for auto-refund</b>';
        text = `Do you want to cancel your appointment with <b>${doctorName}</b>?`;
        primaryButtonTitle = 'Confirm';
        secondaryButtonTitle = 'Close';
        break;
      case 'notEligible':
        heading = 'Request Pending';
        subHeading = 'Unfortunately you are <b>not eligible</b> for an auto-refund';
        text = 'However you can talk to us about your experience and we will get back to you as soon as possible.';
        break;

      case 'notBefore30':
        heading = 'Request Pending';
        subHeading = 'Cancellation is allowed only till <b>30 minutes</b> before the appointment time';
        text = 'However, you can talk to us more about it and we will get back to you as soon as possible';
        break;

      // case 'requestPending2':
      //   heading = 'Request Pending';
      //   subHeading = 'Unfortunately you are <b>not eligible</b> for an auto-refund';
      //   text = 'Your appointment is still active. You can wait for the doctor to join the call';
      //   primaryButtonTitle = 'Join Call';

      //   break;
      // case 'requestPending3':
      //   heading = 'Request Pending';
      //   subHeading = 'Rescheduling is allowed only till <b>30 minutes</b> before the appointment time';
      //   text = 'However, you can talk to us more about it and we will get back to you as soon as possible';
      //   break;
    }

    const children = (
      <Content>
        <BaseBox mb='13px' dangerouslySetInnerHTML={{ __html: subHeading }} />
        <BaseBox mb='17px' dangerouslySetInnerHTML={{ __html: text }} />
        <Footer gutter={8} $requestType={requestType}>
          <PrimaryButton
            disabled={isLoading}
            icon={isLoading ? <Icons.LoadingIcon width={16} /> : ''}
            id={camelCase(primaryButtonTitle)}
            size='small'
            title={primaryButtonTitle}
            onClick={handleBtnClick}
          />
          <SecondaryButton
            disabled={isLoading}
            id={camelCase(secondaryButtonTitle)}
            size='small'
            title={secondaryButtonTitle}
            onClick={handleBtnClick}
          />
        </Footer>
      </Content>
    );

    return { heading, children };
  }

  const { heading, children } = renderedContent();
  return (
    <Modal
      centered
      hideFooter
      hideCrossIcon
      isOpen={!!requestType}
    >
      <Header><Heading3 title={heading} /></Header>
      {children}
    </Modal>
  );
};

export default RequestCancellation;

